import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { isOrgAdminUser } from '../utils/orgRoles';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export function useRuntimeUpdates() {
    const { user, getAuthHeader } = useAuth();
    const [advisories, setAdvisories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [requestingId, setRequestingId] = useState(null);

    const canRequest = Boolean(
        user && (isOrgAdminUser(user) || user.role === 'admin'),
    );

    const load = useCallback(async () => {
        if (!canRequest) {
            setAdvisories([]);
            return;
        }
        setLoading(true);
        try {
            const response = await fetch(`${API}/runtime-updates`, { headers: getAuthHeader() });
            if (!response.ok) {
                const err = new Error('Runtime updates request failed');
                err.status = response.status;
                throw err;
            }
            const payload = await response.json();
            setAdvisories(payload?.data || []);
            setError(null);
        } catch (err) {
            // A 403 is expected for users who are not platform update admins;
            // cosmetic gating is not the authorization boundary.
            if (err?.status !== 403) setError('Unable to load runtime updates');
            setAdvisories([]);
        } finally {
            setLoading(false);
        }
    }, [canRequest, getAuthHeader]);

    useEffect(() => {
        load();
    }, [load]);

    const active = useMemo(() => advisories.find((item) => {
        if (item.state === 'applied' || item.state === 'superseded') return false;
        return true;
    }) || null, [advisories]);

    const requestUpdate = useCallback(async (id) => {
        setRequestingId(id);
        try {
            const response = await fetch(`${API}/runtime-updates/${id}/request`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
                body: '{}',
            });
            const payload = await response.json();
            if (!response.ok) {
                const err = new Error(payload?.detail?.message || 'Unable to queue runtime update');
                err.status = response.status;
                throw err;
            }
            setAdvisories((items) => items.map((item) => item.id === id ? payload?.data || item : item));
            setError(null);
            return payload?.data;
        } catch (err) {
            setError(err?.message || 'Unable to queue runtime update');
            throw err;
        } finally {
            setRequestingId(null);
        }
    }, [getAuthHeader]);

    return { active, advisories, loading, error, canRequest, requestingId, requestUpdate, reload: load };
}
